import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'

import { useVerificationsList } from './hooks'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path } from '../../../libs'
import { Badge, Button, Divider, Table, Tag } from 'antd'
import { CheckCircleOutlined, CloseSquareOutlined } from '@ant-design/icons'
import { Client, Verification } from './types'
import { ColumnsType } from 'antd/lib/table'
import { SystemComment, verificationStatus, verificationType } from '../const'
import CIcon from '@coreui/icons-react'
import { cilPencil } from '@coreui/icons'
import dayjs from 'dayjs'
import { Guard } from '../../../features/session/guard'
import { $countryNameByCode } from '../../../features/dictionaries/model'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import { useUnit } from 'effector-react'
import {
  FILTER_CONFIG,
  VerificationsListFilters,
} from '../widgets/VerificationsListFilters'

export const VerificationsList = () => {
  const countryNameByCode = useUnit($countryNameByCode)
  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const {
    verifications,
    totalCountOfVerifications,
    loading,
    users,
    onChangeVerificationsList,
  } = useVerificationsList()

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const sortField = filters.sortField || 'id'
  const sortOrder = (filters.sortOrder as 'ascend' | 'descend') || 'descend'

  const buildApiArgs = useCallback(() => {
    const searchText: Record<string, any> = {}
    if (filters.requestId) searchText.requestId = filters.requestId
    if (filters.clientId) searchText.clientId = filters.clientId
    if (filters.email) searchText['client.email'] = filters.email
    if (filters.fullName) searchText.fullName = filters.fullName
    if (filters.createdAtFrom && filters.createdAtTo) {
      searchText.createdAt = [
        dayjs(filters.createdAtFrom),
        dayjs(filters.createdAtTo),
      ]
    }

    const apiFilters = {
      status: filters.status?.length ? filters.status : null,
      documentType: filters.documentType?.length ? filters.documentType : null,
      'client.products': filters.products?.length ? filters.products : null,
      country: filters.country?.length ? filters.country : null,
      'user.email': filters.manager?.length ? filters.manager : null,
      'client.hasDeposit':
        filters.hasDeposit === undefined ? null : filters.hasDeposit,
      isDuplicate:
        filters.isDuplicate === undefined ? null : filters.isDuplicate,
      isPremium: filters.isPremium === undefined ? null : filters.isPremium,
    }

    return {
      limit: pageSize,
      page,
      orderBy: sortField,
      orderDirection: sortOrder,
      searchText,
      searchedColumn: '',
      filters: apiFilters,
    }
  }, [filters, page, pageSize, sortField, sortOrder])

  useEffect(() => {
    onChangeVerificationsList(buildApiArgs())
  }, [buildApiArgs])

  const columns: ColumnsType<Verification> = useMemo(
    () => [
      {
        title: 'Request ID',
        dataIndex: 'requestId',
        key: 'requestId',
        sorter: true,
        sortOrder: sortField === 'id' ? sortOrder : undefined,
        render: (requestId: string) => (
          <a href={path.verificationsV2.details(requestId)} target="_blank">
            {requestId}
          </a>
        ),
      },
      {
        title: 'Created at',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        sortOrder: sortField === 'createdAt' ? sortOrder : undefined,
        width: 150,
      },
      {
        title: 'Type',
        dataIndex: 'documentType',
        key: 'documentType',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (item: string, record: Verification) => {
          let tagClass: string | undefined
          switch (item) {
            case verificationStatus.verificationRequestStatusAccepted():
              tagClass = 'success'
              break
            case verificationStatus.verificationRequestStatusRejected():
              tagClass = 'error'
              break
            case verificationStatus.verificationRequestStatusExpired():
              tagClass = 'warning'
              break
            case verificationStatus.verificationRequestStatusNew():
              tagClass = 'processing'
              if (
                record.documentType ===
                verificationType.verificationRequestTypeProfileChange()
              ) {
                tagClass = 'purple'
              }
              if (
                record.documentType ===
                verificationType.verificationRequestTypeUpdatingDocument()
              ) {
                tagClass = 'yellow'
              }
              break
          }
          return (
            <Tag color={tagClass}>
              {record.documentType ===
                verificationType.verificationRequestTypeProfileChange() &&
                item === verificationStatus.verificationRequestStatusNew() &&
                'Profile change'}
              {record.documentType ===
                verificationType.verificationRequestTypeUpdatingDocument() &&
                item === verificationStatus.verificationRequestStatusNew() &&
                'Updated '}
              {record.documentType ===
                verificationType.verificationRequestTypeProfileChange() &&
                item !== verificationStatus.verificationRequestStatusNew() &&
                item}
              {record.documentType !==
                verificationType.verificationRequestTypeProfileChange() && item}
            </Tag>
          )
        },
      },
      {
        title: 'Client ID',
        key: 'clientId',
        render: (item: Verification) => (
          <div>
            {item.client.isTest ? (
              <Badge count={'Test'} offset={[10, -10]}>
                <a href={path.clients.details(item.clientId)} target="_blank">
                  {item.clientId}
                </a>
              </Badge>
            ) : (
              <a href={path.clients.details(item.clientId)} target="_blank">
                {item.clientId}
              </a>
            )}
          </div>
        ),
      },
      {
        title: 'E-mail',
        dataIndex: 'client',
        key: 'client.email',
        render: (client: Client) => <>{client.email}</>,
      },
      {
        title: 'Product',
        dataIndex: 'client',
        key: 'products',
        render: (client: Client) => <>{client?.products?.join(', ')}</>,
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country',
        render: (country: string) => countryNameByCode[country] || country,
      },
      {
        title: 'Full name',
        dataIndex: 'fullName',
        key: 'fullName',
      },
      {
        title: 'Has deposit',
        dataIndex: 'client',
        key: 'hasDeposit',
        render: (client: Client) => (
          <Tag color={client?.hasDeposit ? 'success' : 'error'}>
            {client?.hasDeposit ? 'Yes' : 'No'}
          </Tag>
        ),
      },
      {
        title: 'Manager',
        key: 'manager',
        render: (item: Verification) => (
          <>
            {item.systemComment === SystemComment.AUTO_REJECTED_BY_DUPLICATE &&
            item.status ===
              verificationStatus.verificationRequestStatusRejected() ? (
              <Tag color="warning">Dub-check</Tag>
            ) : (
              <div>{item?.manager?.user?.email}</div>
            )}
          </>
        ),
      },
      {
        title: 'Is duplicate',
        dataIndex: 'isDuplicate',
        key: 'isDuplicate',
        render: (isDuplicate: boolean) =>
          isDuplicate ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          ),
      },
      {
        title: 'Is premium',
        key: 'isPremium',
        render: (item: Verification) =>
          item.client.isPremium ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          ),
      },
      {
        title: 'Actions',
        fixed: 'right',
        width: 133,
        render: (item) => (
          <Guard permission="edit_verification">
            <CButton
              color="warning"
              className="me-2"
              title="Edit"
              target={'_blank'}
              href={path.verificationsV2.edit(item.requestId)}
            >
              <CIcon icon={cilPencil} />
            </CButton>
          </Guard>
        ),
      },
    ],
    [countryNameByCode, sortField, sortOrder],
  )

  const csvContent = verifications
    .map((item: { [s: string]: unknown } | ArrayLike<unknown>) =>
      Object.values(item).join(','),
    )
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  return (
    <CCardBody>
      <Guard permission="downloads_verification_list">
        <CButton
          color="primary"
          className="mb-2"
          href={csvCode}
          download="coreui-table-data.csv"
          target="_blank"
        >
          Download current items (.csv)
        </CButton>
        <Divider />
      </Guard>

      <VerificationsListFilters
        filters={filters}
        setFilters={setFilters as any}
        resetFilters={resetFilters}
        users={users}
      />

      <Table
        dataSource={verifications}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="requestId"
        title={() => (
          <Button
            size="small"
            style={{ marginLeft: 'auto', display: 'block' }}
            onClick={() =>onChangeVerificationsList(buildApiArgs())}
          >
            Update Data
          </Button>
        )}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfVerifications,
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current) {
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          }
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          if (sorter.field) {
            // @ts-ignore
            const field = sorter.field === 'requestId' ? 'id' : sorter.field
            updates.sortField = field === 'id' ? undefined : field
            // @ts-ignore
            updates.sortOrder =
              // @ts-ignore
              sorter.order === 'descend' || !sorter.order ? undefined : 'ascend'
          }
          setFilters(updates as any)
        }}
      />
    </CCardBody>
  )
}
